import { useEffect, useState } from 'react';
import { useGetExperimentConfiguration } from '@/hooks/useApex/useGetExperimentConfiguration';
import { apexConstants } from '@/types/ApexExperimentConstants';
import { useAirKeyTrace, useAirKeyTracePageView } from '@/utils/airKeyTrace';
import * as icons from '@cof/omni-gravity-icons-react';
import { FlexContainer, Icon, Spinner } from '@cof/omni-react';
import { Currency, MerchantInfo } from '../AndroidScreen';

interface TransactionDetailsScreenProps {
  cardLastFour: string;
  merchantLogo: string;
  merchantName: string;
  onApprove: (
    variantName: string,
    experimentKey: string,
    policyProcessId: string,
  ) => void;
  onDecline: (
    variantName: string,
    experimentKey: string,
    policyProcessId: string,
  ) => void;
  purchaseAmount: string;
  processId: string;
}

const {
  transactionDetailExperiments: { experimentKey, controlVariationName },
} = apexConstants;

export const TransactionDetailsScreen = ({
  onApprove,
  onDecline,
  purchaseAmount,
  merchantName,
  merchantLogo,
  cardLastFour,
  processId,
}: TransactionDetailsScreenProps) => {
  const [isVariant, setIsVariant] = useState(false);
  const [variantName, setVariantName] = useState('');
  useAirKeyTracePageView('transaction_details');
  const sendTrace = useAirKeyTrace();

  const { data, error, loading } = useGetExperimentConfiguration({
    policyProcessId: processId,
    experimentKey: [experimentKey],
    userAgent: navigator.userAgent,
  });

  if (error) {
    sendTrace({
      view: 'transaction_details',
      action: 'system_error',
      additionalData: {
        component: 'APEX',
        during_action: 'fetch_fraud_screen',
        error_type: 'api_failure',
        error_code: String(error),
        recovery_action: 'show_error',
        policy_process_id: processId,
        experiment_key: experimentKey,
      },
    });
  }

  useEffect(() => {
    if (
      data &&
      data.activatedExperiments &&
      data.activatedExperiments.length > 0 &&
      data.activatedExperiments[0].variationName
    ) {
      setIsVariant(
        data.activatedExperiments[0].variationName !== controlVariationName,
      );
      setVariantName(data.activatedExperiments[0].variationName);
      sendTrace({
        view: 'transaction_details',
        action: 'view_loaded',
        additionalData: {
          component: 'APEX',
          policy_process_id: processId,
          experiment_key: experimentKey,
          variation_name: data.activatedExperiments[0].variationName,
        },
      });
    } else {
      sendTrace({
        view: 'transaction_details',
        action: 'system_error',
        additionalData: {
          component: 'APEX',
          during_action: 'parse_experience_response',
          error_type: 'invalid_data',
          error_code: 'invalid_experience_response',
          recovery_action: 'show_error',
          policy_process_id: processId,
          experiment_key: experimentKey,
        },
      });
    }
  }, [data, processId, sendTrace]);

  const handleOnApprove = () => {
    sendTrace({
      view: 'transaction_details',
      action: 'confirm',
      additionalData: {
        component: 'APEX',
        policy_process_id: processId,
        experiment_key: experimentKey,
        variation_name: variantName,
      },
    });
    onApprove(variantName, experimentKey, processId);
  };

  const handleOnDecline = () => {
    sendTrace({
      view: 'transaction_details',
      action: 'deny',
      additionalData: {
        component: 'APEX',
        policy_process_id: processId,
        experiment_key: experimentKey,
        variation_name: variantName,
      },
    });
    onDecline(variantName, experimentKey, processId);
  };

  return !loading ? (
    <div className="transaction" data-testid="TransactionDetailsScreen">
      <header>
        <h1 className="header">
          {isVariant
            ? 'Are you sure you would like to proceed with this purchase?'
            : 'Would you like to proceed with this purchase?'}
        </h1>
      </header>
      <div className="container">
        {isVariant ? (
          <div className="alert-card">
            <FlexContainer direction="row" justifyContent={'center'}>
              <Icon
                svg={icons.UiCautionTriangleFilledSmall}
                color="warning"
                marginRight={'small'}
              />
              <div>
                If you approve the purchase you are responsible for the
                completed transaction
              </div>
            </FlexContainer>
          </div>
        ) : (
          <></>
        )}
        <div className="card">
          <div>
            <div className="label">Purchase amount</div>
          </div>
          <div>
            <Currency value={purchaseAmount} />
          </div>
        </div>
        <div className="card">
          <div className="label">Merchant</div>
          <MerchantInfo name={merchantName} logo={merchantLogo} />
        </div>
        <div className="bottom">
          <p>
            Take out your <strong>card ending in {cardLastFour}</strong> before
            proceeding.
          </p>
          <div className="actions">
            <button
              className="button primary"
              onClick={handleOnApprove}
              data-testid="TransactionDetailsScreen--Approve"
            >
              {isVariant ? 'Yes, approve this purchase' : 'Yes, continue'}
            </button>
            <button
              className="button link"
              onClick={handleOnDecline}
              data-testid="TransactionDetailsScreen--Decline"
            >
              {isVariant
                ? 'No, cancel this purchase'
                : "No, I don't recognize this"}
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <Spinner data-testid="TransactionDetails--Spinner" size="giant" />
  );
};
